import type {
  TTrajectorySelectedReferenceTree,
  TTrajectorySelectedReferenceWellbore,
} from '../types/selected-reference-data';

import type { TrajectoryPlanOrFact } from './model';

export type TWellboreTrajectoryRequest = {
  wellboreId: number;
  trajectoryIds: number[];
};

type CollectParams = {
  planOrFact: TrajectoryPlanOrFact | null;
  selectedWellboreId: number | null;
  selectedReferenceData: TTrajectorySelectedReferenceTree;
};

function collectFactWellbore(
  wellbore: TTrajectorySelectedReferenceWellbore,
  ensureWellbore: (wellboreId: number) => void,
  addTrajectory: (wellboreId: number, trajectoryId: number) => void,
): void {
  if (wellbore.trajectories.length === 0) {
    ensureWellbore(wellbore.wellboreId);
    return;
  }

  for (const trajectory of wellbore.trajectories) {
    addTrajectory(wellbore.wellboreId, trajectory.trajectoryId);
  }
}

/** Builds unique wellbore -> trajectoryIds requests for fact-only loading. */
export function collectFactTrajectoryRequests({
  planOrFact,
  selectedWellboreId,
  selectedReferenceData,
}: CollectParams): TWellboreTrajectoryRequest[] {
  if (planOrFact === 'plan') {
    return [];
  }

  const byWellbore = new Map<number, Set<number>>();

  const ensureWellbore = (wellboreId: number) => {
    if (!byWellbore.has(wellboreId)) {
      byWellbore.set(wellboreId, new Set());
    }
  };

  const addTrajectory = (wellboreId: number, trajectoryId: number) => {
    const existing = byWellbore.get(wellboreId) ?? new Set<number>();
    existing.add(trajectoryId);
    byWellbore.set(wellboreId, existing);
  };

  if (planOrFact === 'fact' && selectedWellboreId != null) {
    ensureWellbore(selectedWellboreId);
  }

  for (const well of selectedReferenceData.wells) {
    for (const wellbore of well.fact ?? []) {
      collectFactWellbore(wellbore, ensureWellbore, addTrajectory);
    }
  }

  return [...byWellbore.entries()].map(([wellboreId, trajectoryIds]) => ({
    wellboreId,
    trajectoryIds: [...trajectoryIds],
  }));
}
